/**
 * Module Staff #02 — Gestion des opérations
 * Source : Guide_Admin_Baobab_Connect.pdf · Chapitre 3
 */
const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const IMG = (name) => `${BACKEND}/api/uploads/docs/html_assets/admin/${name}`;

const moduleStaffOperations = {
  id: 'staff-operations',
  population: 'staff',
  order: 2,
  category: 'Opérations',
  title: 'Gestion des opérations',
  subtitle: 'Pilotage complet du cycle de vie d\'une opération de transport',
  icon: 'Truck',
  duration: '35 min',
  level: 'Intermédiaire',
  audience: 'Opérateurs, dispatch, encadrants',
  slides: [
    { type: 'cover', eyebrow: 'Module 02 — Staff', title: 'Gestion des opérations', subtitle: 'De la demande à la clôture, sans accroc', icon: 'Truck', duration: '35 min', audience: 'Opérations', level: 'Intermédiaire' },
    { type: 'intro', title: 'Objectifs', intro: 'En tant qu\'opérateur Baobab, vous orchestrez l\'ensemble du cycle de vie d\'une opération. Ce module couvre toutes les actions clés.', objectives: [
      'Comprendre les 7 statuts du cycle de vie (préparation → exécution → terminée)',
      'Créer une opération depuis 0 ou à partir d\'un devis client',
      'Assigner un véhicule, un chauffeur, un opérateur back-office',
      'Suivre l\'exécution en temps réel et résoudre les blocages',
      'Gérer les annulations et exceptions (refus, incidents, retards)',
      'Utiliser les filtres avancés et exports pour le reporting interne',
    ]},
    { type: 'screenshot', eyebrow: 'Centre opérationnel', title: 'La page Gestion des Opérations',
      image: IMG('img_admin_operations.png'),
      callouts: [
        { label: 'KPIs Phase', text: 'Total / Préparation / Exécution / Facturation / Terminées — cliquables.', x: 50, y: 22 },
        { label: 'Filtres avancés', text: 'Période, statut financier, divergences, type, entreprise, opérateur.', x: 50, y: 45 },
        { label: 'Recherche globale', text: 'Référence, client, trajet, email, n° téléphone.', x: 50, y: 55 },
        { label: 'Liste opérations', text: 'Tri par date / prix / statut ; clic = fiche détaillée.', x: 50, y: 75 },
      ],
    },
    { type: 'concept', eyebrow: 'Cycle de vie', icon: 'BookOpen', title: 'Les 7 statuts à maîtriser',
      lede: 'Une opération circule entre 7 statuts principaux :',
      bullets: [
        '1️⃣ Demande enregistrée : créée mais non encore validée par Baobab',
        '2️⃣ Devis envoyé : prix transmis au client, en attente d\'acceptation',
        '3️⃣ Planifiée : devis accepté, véhicule + chauffeur assignés',
        '4️⃣ Prête au chargement : véhicule à quai, en attente du client',
        '5️⃣ En route : chargement signé, véhicule en mouvement',
        '6️⃣ Livrée : déchargement signé par destinataire',
        '7️⃣ Terminée : facturée + payée + clôturée',
      ],
      note: 'Plus les statuts d\'exception : Annulée (client/admin), En litige, En attente de paiement.',
    },
    { type: 'steps', title: 'Créer une opération en 5 étapes', steps: [
      { title: 'Bouton « + Nouvelle opération »', detail: 'En haut à droite, accessible aux rôles admin/opérateur.' },
      { title: 'Sélectionner le client', detail: 'Recherche par nom d\'entreprise ou particulier ; si nouveau, créer une fiche client.' },
      { title: 'Saisir le trajet & marchandise', detail: 'Départ, arrivée, type marchandise, poids, nombre de véhicules.' },
      { title: 'Définir le devis ou le tarif contrat', detail: 'Si contrat actif : tarif appliqué automatiquement. Sinon : devis manuel.' },
      { title: 'Assigner véhicule + chauffeur + opérateur', detail: 'Le système suggère selon disponibilité et zone.' },
    ]},
    { type: 'steps', title: 'Résoudre un blocage en exécution', steps: [
      { title: 'Identifier le blocage', detail: 'KPI rouge "En retard" ou alerte chauffeur via WhatsApp/app.' },
      { title: 'Ouvrir la fiche opération', detail: 'Timeline complète + dernière position GPS.' },
      { title: 'Diagnostiquer', detail: 'Panne véhicule, incident administratif, refus client, météo...' },
      { title: 'Agir', detail: 'Réassigner, contacter le client, prolonger, déclencher assistance.' },
      { title: 'Mettre à jour le statut', detail: 'Le client voit le nouveau statut + commentaire en temps réel.' },
    ]},
    { type: 'comparison', title: 'Pilotage : bonnes pratiques vs pièges', do: [
      'Mettre à jour les statuts en temps réel (pas en différé)',
      'Toujours ajouter un commentaire sur les changements de statut',
      'Utiliser les vues sauvegardées pour vos opérations critiques',
      'Vérifier la couverture GPS avant assignation du véhicule',
      'Communiquer proactivement avec le client en cas de retard',
    ], dont: [
      'Ne sautez pas l\'étape "Devis envoyé" pour aller plus vite',
      'Ne réassignez pas sans contacter le chauffeur précédent',
      'Ne marquez pas "Livrée" sans signature BD du destinataire',
      'Ne supprimez jamais une opération — annulez-la avec motif',
    ]},
    { type: 'quiz', title: 'Quiz #1',
      question: 'Un chauffeur signale une panne. L\'opération est en statut "En route". Quelle est la bonne séquence d\'actions ?',
      options: [
        'Marquer l\'opération comme "Annulée" et créer une nouvelle',
        'Mettre à jour le statut avec commentaire + réassigner un autre véhicule + notifier le client',
        'Attendre la résolution avant de communiquer',
        'Demander au chauffeur de se débrouiller',
      ],
      answerIndex: 1,
      explanation: 'La transparence + réactivité = critère #1 de satisfaction client. La traçabilité de l\'incident est aussi obligatoire OHADA.',
    },
    { type: 'quiz', title: 'Quiz #2',
      question: 'Quel est le seul statut où vous pouvez encore annuler sans frais pour le client ?',
      options: [
        'Demande enregistrée',
        'Planifiée',
        'En route',
        'Livrée',
      ],
      answerIndex: 0,
      explanation: 'L\'annulation gratuite est possible uniquement avant validation du devis. Après "Planifiée", des frais d\'immobilisation s\'appliquent selon contrat.',
    },
    { type: 'takeaways', title: 'À retenir', items: [
      '7 statuts à mémoriser — c\'est votre langage opérationnel.',
      'Toute action = traçabilité (auteur, timestamp, commentaire).',
      'Mise à jour en temps réel, pas en fin de journée.',
      'Annuler = avec motif, jamais supprimer.',
      'Communication client proactive = +30 % de NPS.',
    ]},
    { type: 'cta', title: 'Module 02 terminé !', subtitle: 'Vous orchestrez le pipeline opérationnel.',
      actions: [
        { label: 'Ouvrir les opérations', href: '/admin/operations' },
        { label: 'Module Bons de chargement', href: '/docs/admin/training/staff-bons-chargement' },
        { label: 'Catalogue', href: '/docs/admin/training' },
      ],
    },
  ],
};

export default moduleStaffOperations;
